import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Star, Plus } from "lucide-react";
import { resolveImg } from "@/lib/api";
import { ProductModal } from "./ProductModal";

export const Products = ({ products, content }) => {
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("Todos");

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category));
    return ["Todos", ...Array.from(set)];
  }, [products]);

  const filtered = filter === "Todos" ? products : products.filter((p) => p.category === filter);

  return (
    <section id="catalogo" data-testid="catalog-section" className="relative bg-sand py-24 md:py-32 grain">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="text-xs tracking-[0.25em] uppercase font-semibold text-terracotta-500">Nuestro Catálogo</span>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light tracking-tight text-forest-900 mt-4 max-w-xl leading-[1.05]">
              Productos que transforman tu bienestar
            </h2>
          </div>
          <p className="text-forest-700/70 max-w-sm text-base md:text-lg">
            Productos naturales y orgánicos de la más alta calidad, formulados con Ganoderma y superalimentos.
          </p>
        </div>

        {/* Category filters */}
        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 mb-12">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c}`}
              onClick={() => setFilter(c)}
              className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-medium transition-all border ${
                filter === c
                  ? "bg-forest-900 text-sand border-forest-900"
                  : "bg-transparent text-forest-800 border-forest-900/15 hover:border-forest-900/40"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 md:gap-8">
          {filtered.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`product-card-${p.id}`}
              onClick={() => setActive(p)}
              className="group cursor-pointer break-inside-avoid mb-6 md:mb-8"
            >
              <div className="relative overflow-hidden rounded-2xl bg-forest-900 shadow-[0_8px_32px_rgba(26,54,38,0.06)]">
                <img
                  src={resolveImg(p.image)}
                  alt={p.name}
                  loading="lazy"
                  className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-4 right-4 h-11 w-11 rounded-full bg-terracotta-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                  <Plus size={20} />
                </div>
              </div>
              <div className="mt-5 flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[10px] tracking-[0.18em] uppercase font-semibold text-terracotta-500">{p.category}</span>
                    <span className="flex items-center gap-1 text-forest-700">
                      <Star size={12} fill="currentColor" className="text-terracotta-500" />
                      <span className="text-xs font-semibold">{p.rating}</span>
                    </span>
                  </div>
                  <h3 className="font-serif text-xl text-forest-900 leading-tight group-hover:text-terracotta-600 transition-colors">
                    {p.name}
                  </h3>
                </div>
                <div className="font-serif text-2xl text-forest-900 shrink-0">${p.price.toFixed(0)}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <ProductModal product={active} content={content} onClose={() => setActive(null)} />
    </section>
  );
};
